import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Card, ActionsDiv, FormGrid, FormGroup, Label,
  Select, HeaderLine, Td, Th, Tr, InputLabel, Summary, SummaryItem, SummaryLabel,
  SummaryValue, Separator, ButtonsDiv, AddButton, AddButtonImg, AddButtonText,
  CancelButton, CancelButtonText, RemoveButton, RemoveButtonImg,
  RemoveButtontext
} from './style';
import GlobalLoadingOverlay from '../../../../components/GlobalLoadingOverlay';
import whitePlus from '../../../../assets/fashion/TableFashionHeader/whitePlus.svg';
import remove from '../../../../assets/fashion/Actions/remove.svg';
import { Outlet } from 'react-router-dom';
import axios from '../../../../config/axios';

let cachedCollections: any[] | null = null;
let cachedCollectionsPromise: Promise<any[]> | null = null;

const cachedGroupsByCollection = new Map<number, any[]>();
const cachedFamiliesByCollection = new Map<number, any[]>();
const cachedGroupsByFamily = new Map<number, any[]>();

interface FashionOrderProductItemInput {
  colorId: number;
  size: string;
  quantity: number;
}


interface ColorData {
  id: number;
  name: string;
}

interface ProductApiData {
  id: number;
  fashionProductId: number;
  discount: number | null;
  totalItems: number;
  subtotal: number;
  items: FashionOrderProductItemInput[];
  size?: string[];

  fashionOrderId?: number;
  fashionProduct?: {
    id: number;
    name: string;
    price: number;
    size: string[];
    collection?: { id: number; name: string };
    group: { id: number; name?: string; family?: { id: number; name?: string; collectionId?: number; } };
  };
  colorCount?: number;
  colors?: string[];
  family?: { id: number; Corileone?: { name: string }; name?: string; familyId?: number; };
  group?: { id: number; name?: string };
  name?: string;
  price?: number;
  totalQuantity?: number;
  totalSubtotal?: number;
  selectedColors?: number[];
  valorFinal?: number;
}

interface CollectionData {
  id: number;
  name: string;
}

interface FamilyData {
  id: number;
  name: string;
  collectionId?: number;
  groups?: GroupData[];
  colors?: ColorData[];
}

interface GroupData {
  id: number;
  name: string;
  products?: ProductApiData[];
}

interface PropsType {
  order: any[];
  setOrder: React.Dispatch<React.SetStateAction<any>>;
  product?: any;
  onClose?: () => void;
  onRemoveProduct: (index: number) => void;
  onProductUpdated?: (oldProduct: ProductApiData, updatedProduct: ProductApiData) => void;
  onProductAdded?: any;
  isEditing: boolean;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const BASE = process.env.REACT_APP_GATEWAY_BASE_URL;

const PedidoForm: React.FC<PropsType> = ({ order, setOrder, product, onClose, onRemoveProduct, onProductUpdated, onProductAdded, isEditing }) => {
  const renderCountRef = useRef(0)
  const lastRenderAtRef = useRef(Date.now())

  const [allProductsData, setAllProductsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  renderCountRef.current += 1
  const nowRender = Date.now()
  console.log('🔁 [AddProductForm] render', renderCountRef.current, { isEditing, productId: product?.fashionProductId, durationSinceLastRender: nowRender - lastRenderAtRef.current, allProductsLength: allProductsData.length, isLoading })
  lastRenderAtRef.current = nowRender

  const [collectionsData, setCollectionsData] = useState<CollectionData[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [collectionGroupsData, setCollectionGroupsData] = useState<GroupData[]>([]);
  const [selectedCollectionGroupId, setSelectedCollectionGroupId] = useState<string>('');
  const [familiesData, setFamiliesData] = useState<FamilyData[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [familyGroupsData, setFamilyGroupsData] = useState<GroupData[]>([]);
  const [selectedFamilyGroupId, setSelectedFamilyGroupId] = useState<string>('');

  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [availableColors, setAvailableColors] = useState<ColorData[]>([]);
  const [selectedColors, setSelectedColors] = useState<{ [colorId: number]: boolean }>({});
  const [quantities, setQuantities] = useState<Map<number, { [size: string]: number }>>(new Map());
  const [discount, setDiscount] = useState<string>('');
  const [valorFinal, setValorFinal] = useState<number | null>(null);
  const valorFinalOverrideRef = useRef(false);

  const skipCascadeReset = useRef(false);
  const skipProductColorReset = useRef(false);
  const lastSelectedProductIdRef = useRef<number | null>(null);
  const pendingFamilyGroupId = useRef<string | null>(null);
  const pendingCollectionGroupId = useRef<string | null>(null);

  const getItemColorId = (item: any): number | null => {
    if (!item || typeof item !== 'object') return null;
    const keys = ['colorId', 'color_id', 'color', 'itemColorId', 'item_color_id', 'collectionColorId', 'collection_color_id', 'corId', 'cor_id'];
    for (const key of keys) {
      if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
        const parsed = Number(item[key]);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return null;
  };

  const getItemSize = (item: any): string => {
    if (!item || typeof item !== 'object') return '';
    const keys = ['size', 'tamanho', 'grade', 'gradeId', 'grade_id', 'sizeId', 'tam'];
    for (const key of keys) {
      const val = item[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val);
      }
    }
    return '';
  };

  const getItemQuantity = (item: any): number => {
    if (!item || typeof item !== 'object') return 0;
    const keys = ['quantity', 'quantidade', 'qty', 'qtd'];
    for (const key of keys) {
      const val = item[key];
      const num = Number(val);
      if (!Number.isNaN(num)) return num;
    }
    return 0;
  };

  const calculatedTotalQuantity = useMemo(() => {
    let sum = 0;
    quantities.forEach(sizeMap => Object.values(sizeMap).forEach(qty => (sum += qty)));
    return sum;
  }, [quantities]);

  const totalSelectedColors = useMemo(() => Object.values(selectedColors).filter(Boolean).length, [selectedColors]);


  const calculatedTotalSubtotal = useMemo(() => {
    if (selectedPrice === null) return 0;
    return calculatedTotalQuantity * selectedPrice;
  }, [calculatedTotalQuantity, selectedPrice]);


  const computedValorFinal = useMemo(() => {
    if (valorFinal !== null) return valorFinal;
    const raw = calculatedTotalSubtotal;
    const disc = parseFloat(discount);
    if (!isNaN(disc) && disc > 0) {
      return raw - (disc / 100) * raw;
    }
    return raw;
  }, [valorFinal, calculatedTotalSubtotal, discount]);

  useEffect(() => {
    if (!valorFinalOverrideRef.current) {
      setValorFinal(null);
    }
  }, [calculatedTotalQuantity, selectedPrice, discount]);

  const alreadyAddedProductIds = useMemo(() => {
    const ids = new Set<number>();
    order.forEach(item => {
      const id = Number(item.fashionProductId ?? item.id ?? item?.fashionProduct?.id);
      if (!Number.isNaN(id) && id > 0) ids.add(id);
    });
    console.debug('[AddProductForm] alreadyAddedProductIds computed', { orderLength: order.length, ids: Array.from(ids) });
    return ids;
  }, [order]);

  const filteredProducts = useMemo(() => {
    let prods = allProductsData.filter(p => p.status !== false);

    console.debug('[filteredProducts] start', {
      allProductsCount: allProductsData.length,
      selectedCollectionId,
      selectedCollectionGroupId,
      selectedFamilyId,
      selectedFamilyGroupId,
      allProductsSample: allProductsData.slice(0, 2),
      alreadyAddedProductIds: Array.from(alreadyAddedProductIds),
    });

    const editingProductId = Number(product?.fashionProductId ?? product?.fashionProduct?.id) || null;
    const excludedProductIds = new Set(alreadyAddedProductIds);
    if (editingProductId) excludedProductIds.delete(editingProductId);
    if (selectedProductId) excludedProductIds.delete(selectedProductId);
    if (excludedProductIds.size > 0) {
      const before = prods.length;
      prods = prods.filter(p => !excludedProductIds.has(p.id));
      console.debug('[filteredProducts] after excluding already added products', { before, after: prods.length, excludedProductIds: Array.from(excludedProductIds) });
    }

    if (selectedCollectionId) {
      const before = prods.length;
      prods = prods.filter(p => p.collectionId === parseInt(selectedCollectionId));
      console.debug('[filteredProducts] after collection filter', { before, after: prods.length });
    }

    if (selectedCollectionGroupId) {
      const before = prods.length;

      const famIdsInGroup = familiesData
        .filter((f: any) => {
          const groupInFamily = f.GroupInFamily || [];
          const has = groupInFamily.some((gif: any) => gif.groupId === parseInt(selectedCollectionGroupId));
          return has;
        })
        .map((f: any) => f.id);

      console.debug('[filteredProducts] group->family ids computed', {
        selectedCollectionGroupId,
        famIdsInGroup,
        familiesDataSample: familiesData.slice(0, 2),
      });

      prods = prods.filter(p => famIdsInGroup.includes(p.familyId));
      console.debug('[filteredProducts] after collection group filter', { before, after: prods.length });
    }

    if (selectedFamilyId) {
      const before = prods.length;
      prods = prods.filter(p => p.familyId === parseInt(selectedFamilyId));
      console.debug('[filteredProducts] after family filter', { before, after: prods.length });
    }

    if (selectedFamilyGroupId) {
      const before = prods.length;
      prods = prods.filter(p => p.groupId === parseInt(selectedFamilyGroupId));
      console.debug('[filteredProducts] after family group filter', { before, after: prods.length });
    }

    if (selectedProductId) {
      const hasSelected = prods.some(p => p.id === selectedProductId);
      if (!hasSelected) {
        const selectedProduct = allProductsData.find(p => p.id === selectedProductId);
        if (selectedProduct) {
          prods = [selectedProduct, ...prods];
        }
      }
    }

    console.debug('[filteredProducts] final count', prods.length);
    return prods;
  }, [allProductsData, selectedCollectionId, selectedCollectionGroupId, familiesData, selectedFamilyId, selectedFamilyGroupId, selectedProductId, alreadyAddedProductIds, product]);

  const availableSizes = useMemo(() => {
    if (!selectedProductId) return [];

    const p = allProductsData.find(prod => prod.id === selectedProductId);

    const sizesFromBack = (() => {
      if (!p) return [] as string[];
      const fromGrades: string[] = ((p as any).GradeInProduct || []).map((g: any) => g.Grade?.name).filter(Boolean);
      if (fromGrades.length > 0) return fromGrades;
      return (p.size as string[]) || [];
    })();

    const sizesFromItems = (() => {
      if (!product || !Array.isArray(product.items)) return [] as string[];
      const set = new Set<string>();
      product.items.forEach((item: any) => {
        const size = String(item.size || '').trim();
        if (size) set.add(size);
      });
      return Array.from(set);
    })();

    const merged = Array.from(new Set([...sizesFromBack, ...sizesFromItems]));
    if (merged.length === 0) {
      console.log('[AddProductForm] availableSizes vazio. sizesFromBack:', sizesFromBack, 'sizesFromItems:', sizesFromItems, 'product.items:', product?.items);
    } else {
      console.log('[AddProductForm] availableSizes calculado:', merged, { sizesFromBack, sizesFromItems });
    }
    return merged;
  }, [selectedProductId, allProductsData, product]);

  useEffect(() => {
    console.log('[AddProductForm] availableColors/availableSizes update', {
      availableColorsLength: availableColors.length,
      availableSizesLength: availableSizes.length,
      availableColors,
      availableSizes,
      selectedColors,
    });
  }, [availableColors, availableSizes, selectedColors]);

  useEffect(() => {
    const timerLabel = `[AddProductForm] loadProductsAndCollections ${Date.now()}`
    console.time(timerLabel)
    let mounted = true;
    setIsLoading(true);

    const loadProducts = async () => {
      const productTimer = `[AddProductForm] fetchProducts ${Date.now()}`
      console.time(productTimer)
      try {
        const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product?minimal=true`)
        const prods = Array.isArray(res.data) ? res.data : [];
        console.log('📦 [AddProductForm] allProductsData carregado:', prods.length, 'produtos. Exemplo:', prods[0]);
        return prods;
      } catch (err) {
        console.error('📦 [AddProductForm] erro ao carregar produtos:', err);
        return [] as any[];
      } finally {
        console.timeEnd(productTimer)
      }
    };

    const loadCollections = async () => {
      if (cachedCollections && cachedCollections.length === 0) {
        console.log('📦 [AddProductForm] cache de coleções vazio detectado, forçando recarga');
        cachedCollections = null;
        cachedCollectionsPromise = null;
      }

      if (cachedCollections) {
        console.log('📦 [AddProductForm] usando cache de coleções', cachedCollections.length);
        return cachedCollections;
      }
      if (cachedCollectionsPromise) {
        console.log('📦 [AddProductForm] usando promise em andamento para coleções');
        return cachedCollectionsPromise;
      }

      const collectionTimer = `[AddProductForm] fetchCollections ${Date.now()}`
      console.time(collectionTimer)

      cachedCollectionsPromise = axios
        .get(`${BASE}/backend-fashion-orders/fashion-product/collections`)
        .then(res => {
          const cols = Array.isArray(res.data) ? res.data : [];
          console.log('📦 [AddProductForm] collectionsData carregado:', cols.length, 'coleções:', cols);
          cachedCollections = cols;
          return cols;
        })
        .catch((err) => {
          console.error('📦 [AddProductForm] erro ao carregar coleções:', err);
          cachedCollections = [];
          return [] as any[];
        })
        .finally(() => {
          console.timeEnd(collectionTimer)
        });

      return cachedCollectionsPromise;
    };

    const load = async () => {
      try {
        const [prods, cols] = await Promise.all([loadProducts(), loadCollections()]);
        if (!mounted) return;
        console.log('📦 [AddProductForm] loadProductsAndCollections finalizado', { prodsCount: prods.length, colsCount: cols.length });
        setAllProductsData(prods);
        setCollectionsData(cols);
      } catch (err) {
        console.error('📦 [AddProductForm] erro no loadProductsAndCollections:', err);
      } finally {
        if (mounted) setIsLoading(false);
        console.timeEnd(timerLabel);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingFamilies, setIsLoadingFamilies] = useState(false);

  useEffect(() => {
    const shouldSkip = skipCascadeReset.current;

    if (!shouldSkip) {
      setSelectedCollectionGroupId('');
      setCollectionGroupsData([]);
      setSelectedFamilyId('');
      setFamiliesData([]);
      setFamilyGroupsData([]);
      setSelectedFamilyGroupId('');
      setSelectedProductId(null);
      setSelectedPrice(null);
      setAvailableColors([]);
      setSelectedColors({});
      setQuantities(new Map());
    }

    if (!selectedCollectionId) return;

    console.log('🔵 [AddProductForm] selectedCollectionId mudou para:', selectedCollectionId, '| collectionsData atual:', collectionsData);
    console.time('[AddProductForm] fetchGroupsAndFamilies for collection')

    const fetchGroups = async () => {
      const collectionIdNum = Number(selectedCollectionId);
      if (cachedGroupsByCollection.has(collectionIdNum)) {
        return cachedGroupsByCollection.get(collectionIdNum) || [];
      }

      setIsLoadingGroups(true);
      const timerLabel = `[AddProductForm] fetchGroups ${collectionIdNum} ${Date.now()}`
      console.time(timerLabel)
      try {
        const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product/collections/${collectionIdNum}/groups`);
        const groups = (Array.isArray(res.data) ? res.data : []).map((g: any) => g.Group || g).filter(Boolean);
        cachedGroupsByCollection.set(collectionIdNum, groups);
        return groups;
      } finally {
        setIsLoadingGroups(false);
        console.timeEnd(timerLabel)
      }
    };

    const fetchFamilies = async () => {
      const collectionIdNum = Number(selectedCollectionId);
      if (cachedFamiliesByCollection.has(collectionIdNum)) {
        return cachedFamiliesByCollection.get(collectionIdNum) || [];
      }

      setIsLoadingFamilies(true);
      const timerLabel = `[AddProductForm] fetchFamilies ${collectionIdNum} ${Date.now()}`
      console.time(timerLabel)
      try {
        const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product/families?collectionId=${collectionIdNum}`);
        const families = Array.isArray(res.data) ? res.data : [];
        cachedFamiliesByCollection.set(collectionIdNum, families);
        return families;
      } finally {
        setIsLoadingFamilies(false);
        console.timeEnd(timerLabel)
      }
    };

    Promise.all([fetchGroups(), fetchFamilies()])
      .then(([groups, families]) => {
        setCollectionGroupsData(groups);
        if (pendingCollectionGroupId.current) {
          setSelectedCollectionGroupId(pendingCollectionGroupId.current);
          pendingCollectionGroupId.current = null;
        }
        setFamiliesData(families);
      })
      .catch(console.error)
      .finally(() => {
        console.timeEnd('[AddProductForm] fetchGroupsAndFamilies for collection');
      });
  }, [selectedCollectionId]);

  useEffect(() => {
    const shouldSkip = skipCascadeReset.current;
    skipCascadeReset.current = false;

    if (!shouldSkip) {
      setSelectedFamilyGroupId('');
      setFamilyGroupsData([]);
      setSelectedProductId(null);
      setSelectedPrice(null);
      setAvailableColors([]);
      setSelectedColors({});
      setQuantities(new Map());
    }

    if (!selectedFamilyId) return;

    console.log('🔵 [AddProductForm] selectedFamilyId mudou para:', selectedFamilyId);
    console.time('[AddProductForm] fetchFamilyGroups for family')

    const fetchFamilyGroups = async () => {
      const familyIdNum = Number(selectedFamilyId);
      if (cachedGroupsByFamily.has(familyIdNum)) {
        return cachedGroupsByFamily.get(familyIdNum) || [];
      }

      const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product/groups?familyId=${familyIdNum}`);
      const groups = Array.isArray(res.data) ? res.data : [];
      cachedGroupsByFamily.set(familyIdNum, groups);
      return groups;
    };

    fetchFamilyGroups()
      .then(groups => {
        setFamilyGroupsData(groups);
        if (pendingFamilyGroupId.current) {
          setSelectedFamilyGroupId(pendingFamilyGroupId.current);
          pendingFamilyGroupId.current = null;
        }
      })
      .catch(console.error)
      .finally(() => {
        console.timeEnd('[AddProductForm] fetchFamilyGroups for family');
      });
  }, [selectedFamilyId]);

  useEffect(() => {
    console.log('🧠 [AddProductForm] useEffect(isEditing/product/allProductsData)', { isEditing, productId: product?.fashionProductId, allProductsLength: allProductsData.length });
    if (!isEditing || !product || allProductsData.length === 0) return;

    console.time('[AddProductForm] sync product selection');
    const productId = product.fashionProductId || product.fashionProduct?.id;
    if (!productId) {
      console.timeEnd('[AddProductForm] sync product selection');
      return;
    }

    const found = allProductsData.find(p => p.id === productId);
    console.log('[AddProductForm] sync product selection data', {
      productId,
      found: Boolean(found),
      foundId: found?.id,
      productColors: product.colors,
      productSelectedColors: product.selectedColors,
      productItemsCount: Array.isArray(product.items) ? product.items.length : 0,
    });
    console.log(
      '[AddProductForm] sync product raw data',
      JSON.stringify(
        {
          itemKeys: Array.isArray(product.items) && product.items.length > 0 ? Object.keys(product.items[0]) : [],
          itemsCount: Array.isArray(product.items) ? product.items.length : 0,
          colors: product.colors,
          selectedColors: product.selectedColors,
          itemSample: product.items && product.items.length > 0 ? product.items[0] : null,
        },
        null,
        2,
      ),
    );

    skipCascadeReset.current = true;
    skipProductColorReset.current = true;
    setSelectedProductId(productId);
    lastSelectedProductIdRef.current = productId;
    setSelectedPrice(found?.price ?? product.fashionProduct?.price ?? 0);
    setDiscount(String(product.discount || ''));
    setValorFinal(product.valorFinal ?? null);
    if (found?.collectionId) setSelectedCollectionId(String(found.collectionId));
    if (found?.familyId) setSelectedFamilyId(String(found.familyId));
    if (found?.groupId) {
      pendingFamilyGroupId.current = String(found.groupId);
      pendingCollectionGroupId.current = String(found.groupId);
    }

    const syncAvailableColorsFromProduct = () => {
      const colorMap = new Map<number, string>();

      const addColor = (id: number, name: string) => {
        if (Number.isNaN(id)) return;
        if (!colorMap.has(id)) colorMap.set(id, name);
      };

      if (Array.isArray(product.selectedColors)) {
        const colorsArray = Array.isArray(product.colors) ? product.colors : [];
        product.selectedColors.forEach((c: any, index: number) => {
          const id = Number(c);
          if (Number.isNaN(id)) return;

          let name = '';
          const maybe = colorsArray[index];
          if (maybe !== undefined && maybe !== null) {
            if (typeof maybe === 'object') {
              name = String(maybe.name ?? maybe.label ?? maybe.value ?? id);
            } else {
              name = String(maybe);
            }
          }
          if (!name) name = String(id);
          addColor(id, name);
        });
      }

      ((found as any)?.ProductItemColor || []).forEach((pic: any) => {
        const id = pic?.CollectionColor?.id ?? pic?.collectionColorId;
        if (id === undefined || id === null) return;
        const name = pic?.CollectionColor?.name ?? String(id);
        addColor(Number(id), name);
      });

      (product.items || [])
        .map((item: any) => getItemColorId(item))
        .filter((id: number | null) => id !== null)
        .forEach((id: number | null) => {
          const numericId = Number(id);
          if (Number.isNaN(numericId)) return;
          if (!colorMap.has(numericId)) {
            addColor(numericId, String(numericId));
          }
        });

      const productColors: ColorData[] = Array.from(colorMap.entries()).map(([id, name]) => ({ id, name }));
      setAvailableColors(productColors);

      if (Array.isArray(product.selectedColors) && product.selectedColors.length > 0) {
        const selectedMap: { [colorId: number]: boolean } = {};
        product.selectedColors.forEach((colorId: any) => {
          const parsed = Number(colorId);
          if (!Number.isNaN(parsed)) selectedMap[parsed] = true;
        });
        setSelectedColors(selectedMap);
      }
    };

    if (found) {
      syncAvailableColorsFromProduct();
    } else {
      syncAvailableColorsFromProduct();
    }

    console.timeEnd('[AddProductForm] sync product selection');

    if (product.items && Array.isArray(product.items)) {
      const initColors: { [id: number]: boolean } = {};
      const initQty = new Map<number, { [size: string]: number }>();
      product.items.forEach((item: any) => {
        const colorId = Number(item.colorId);
        const size = String(item.size || '');
        if (Number.isNaN(colorId) || !size) return;

        initColors[colorId] = true;
        if (!initQty.has(colorId)) initQty.set(colorId, {});
        initQty.get(colorId)![size] = Number(item.quantity) || 0;
      });
      setSelectedColors(initColors);
      setQuantities(initQty);
    }
  }, [isEditing, product, allProductsData]);

  useEffect(() => {
    console.log('[AddProductForm] selectedProductId effect', {
      selectedProductId,
      skipProductColorReset: skipProductColorReset.current,
      lastSelectedProductId: lastSelectedProductIdRef.current,
      availableColorsLength: availableColors.length,
    });

    if (skipProductColorReset.current) {
      skipProductColorReset.current = false;
      return;
    }

    if (selectedProductId === lastSelectedProductIdRef.current && availableColors.length > 0) {
      return;
    }

    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());

    if (!selectedProductId) {
      lastSelectedProductIdRef.current = selectedProductId;
      return;
    }

    const found = allProductsData.find(p => p.id === selectedProductId);
    if (!found) {
      lastSelectedProductIdRef.current = selectedProductId;
      return;
    }

    const seen = new Set<number>();
    const productColors: ColorData[] = ((found as any).ProductItemColor || [])
      .map((pic: any) => ({
        id: pic.CollectionColor?.id ?? pic.collectionColorId,
        name: pic.CollectionColor?.name ?? String(pic.collectionColorId),
      }))
      .filter((c: ColorData) => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });

    setAvailableColors(productColors);
    lastSelectedProductIdRef.current = selectedProductId;
  }, [selectedProductId, allProductsData]);

  const fetchFullProduct = async (id: number) => {
    try {
      const res = await axios.get(`${BASE}/backend-fashion-orders/fashion-product/${id}`);
      const prod = res.data;
      setAllProductsData(prev => {
        const idx = prev.findIndex(p => p.id === prod.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = prod;
          return next;
        }
        return [prod, ...prev];
      });
      return prod;
    } catch (err) {
      console.error('[AddProductForm] erro ao buscar produto completo:', err);
      return null;
    }
  };

  const handleProductSelectChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value) || null;
    console.log('[AddProductForm] Produto selecionado id:', id);

    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
    setValorFinal(null);

    let p = id ? allProductsData.find(prod => prod.id === id) : null;

    // Some API responses (when using minimal=true) do not return colors/sizes.
    // Fetch full product details when needed.
    const needsFullFetch = Boolean(
      id &&
      (!p ||
        !Array.isArray((p as any).ProductItemColor) ||
        (p as any).ProductItemColor.length === 0 ||
        !Array.isArray((p as any).GradeInProduct) ||
        ((p as any).GradeInProduct.length === 0 && !(p as any).size?.length))
    );

    if (needsFullFetch && id) {
      const full = await fetchFullProduct(id);
      if (full) {
        p = full;
      }
    }

    console.log('[AddProductForm] Produto encontrado:', p);
    console.log('[AddProductForm] familyId:', p?.familyId, '| Family:', (p as any)?.Family, '| family:', (p as any)?.family, '| size:', p?.size, '| price:', p?.price);

    setSelectedPrice(p?.price ?? 0);
    setSelectedProductId(id);
    setQuantities(new Map());
    setSelectedColors({});
  };

  const buildItemsToSubmit = () => {
    const items: FashionOrderProductItemInput[] = [];
    let total = 0;

    quantities.forEach((sizeMap, colorId) => {
      if (!availableColors.find(c => c.id === colorId)) return;
      Object.entries(sizeMap).forEach(([size, quantity]) => {
        if (quantity > 0) {
          items.push({ colorId, size, quantity });
          total += quantity;
        }
      });
    });

    Object.keys(selectedColors).forEach(k => {
      const colorId = parseInt(k);
      if (selectedColors[colorId] && !items.some(i => i.colorId === colorId)) {
        items.push({ colorId, size: 'N/A', quantity: 0 });
      }
    });

    return { items, total };
  };

  const buildFashionProductPayload = (prod: any) => {
    const collectionFromDropdown = collectionsData.find(c => c.id === parseInt(selectedCollectionId));

    console.log('🏗️ [buildFashionProductPayload] prod recebido:', prod);
    console.log('🏗️ [buildFashionProductPayload] prod.Collection:', prod.Collection);
    console.log('🏗️ [buildFashionProductPayload] prod.Family?.Collection:', prod.Family?.Collection);
    console.log('🏗️ [buildFashionProductPayload] selectedCollectionId:', selectedCollectionId);
    console.log('🏗️ [buildFashionProductPayload] collectionFromDropdown:', collectionFromDropdown);
    console.log('🏗️ [buildFashionProductPayload] collectionsData:', collectionsData);

    const resolvedCollection =
      prod.Collection
        ? { id: prod.Collection.id, name: prod.Collection.name }
        : prod.Family?.Collection
          ? { id: prod.Family.Collection.id, name: prod.Family.Collection.name }
          : collectionFromDropdown
            ? { id: collectionFromDropdown.id, name: collectionFromDropdown.name }
            : undefined;

    console.log('🏗️ [buildFashionProductPayload] resolvedCollection:', resolvedCollection);

    return ({
      id: prod.id,
      name: prod.name || '',
      price: selectedPrice ?? 0,
      size: prod.size || [],
      collection: resolvedCollection,
      group: {
        id: prod.Group?.id || prod.groupId || 0,
        name: prod.Group?.name || '',
        family: prod.Family
          ? {
            id: prod.Family.id,
            name: prod.Family.name,
            collectionId: prod.collectionId || prod.Family?.collectionId,
          }
          : undefined,
      },
    });
  };

  const handleAddProduct = () => {
    const selectedProduct = allProductsData.find(p => p.id === selectedProductId);

    if (!selectedProduct || totalSelectedColors === 0) {
      alert('Selecione um produto e pelo menos uma cor.');
      return;
    }

    const alreadyAdded = order.some(item => {
      const id = Number(item.fashionProductId ?? item.id ?? item?.fashionProduct?.id);
      return id === selectedProduct.id;
    });
    if (alreadyAdded) {
      alert('Este produto já foi adicionado ao pedido.');
      return;
    }

    const { items: itemsToSubmit, total: calculatedTotalItems } = buildItemsToSubmit();

    const newProductToAdd: ProductApiData = {
      id: 0,
      fashionProductId: selectedProduct.id,
      discount: parseFloat(discount) || 0,
      totalItems: calculatedTotalItems,
      subtotal: computedValorFinal,
      valorFinal: computedValorFinal,
      items: itemsToSubmit,
      selectedColors: Object.keys(selectedColors)
        .filter(k => selectedColors[parseInt(k)])
        .map(k => parseInt(k)),
      colors: availableColors.filter(c => selectedColors[c.id]).map(c => c.name),
      fashionProduct: buildFashionProductPayload(selectedProduct),
      family: selectedProduct.Family
        ? { id: selectedProduct.Family.id, name: selectedProduct.Family.name, familyId: selectedProduct.Family.id }
        : undefined,
      group: selectedProduct.Group
        ? { id: selectedProduct.Group.id, name: selectedProduct.Group.name }
        : undefined,
      fashionOrderId: product?.fashionOrderId,
    };

    console.log('✅ [handleAddProduct] newProductToAdd montado:', newProductToAdd);
    console.log('✅ [handleAddProduct] fashionProduct.collection:', newProductToAdd.fashionProduct?.collection);

    if (onProductAdded) {
      onProductAdded(newProductToAdd);
    } else {
      setOrder((prev: any[]) => [...prev, newProductToAdd]);
    }

    setSelectedProductId(null);
    setSelectedCollectionId('');
    setSelectedCollectionGroupId('');
    setSelectedFamilyId('');
    setSelectedFamilyGroupId('');
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
    setDiscount('');
    onClose?.();
  };

  const buildUpdatedProduct = () => {
    const selectedProductData = allProductsData.find(p => p.id === selectedProductId);
    if (!selectedProductData) return null;

    const { items: itemsToSubmit, total: calculatedTotalItems } = buildItemsToSubmit();

    return {
      ...(product as ProductApiData),
      fashionProductId: selectedProductData.id,
      discount: parseFloat(discount) || 0,
      totalItems: calculatedTotalItems,
      subtotal: computedValorFinal,
      valorFinal: computedValorFinal,
      items: itemsToSubmit,
      selectedColors: Object.keys(selectedColors)
        .filter(k => selectedColors[parseInt(k)])
        .map(k => parseInt(k)),
      colors: availableColors.filter(c => selectedColors[c.id]).map(c => c.name),
      fashionProduct: {
        ...(product?.fashionProduct),
        ...buildFashionProductPayload(selectedProductData),
      },
      family: selectedProductData.Family
        ? { id: selectedProductData.Family.id, name: selectedProductData.Family.name, familyId: selectedProductData.Family.id }
        : undefined,
      group: selectedProductData.Group
        ? { id: selectedProductData.Group.id, name: selectedProductData.Group.name }
        : undefined,
    } as ProductApiData;
  };

  const areProductsEqual = (a: ProductApiData, b: ProductApiData) => {
    if (!a || !b) return false;
    const safe = (x: any) => (x === undefined || x === null ? '' : x);
    const sameItems = JSON.stringify(a.items || []) === JSON.stringify(b.items || []);
    const sameSelectedColors = JSON.stringify(a.selectedColors || []) === JSON.stringify(b.selectedColors || []);
    const sameColors = JSON.stringify(a.colors || []) === JSON.stringify(b.colors || []);
    return (
      safe(a.totalItems) === safe(b.totalItems) &&
      safe(a.subtotal) === safe(b.subtotal) &&
      safe(a.valorFinal) === safe(b.valorFinal) &&
      sameItems &&
      sameSelectedColors &&
      sameColors
    );
  };


  const handleUpdateProduct = () => {
    if (totalSelectedColors === 0) {
      alert('Selecione um produto e pelo menos uma cor com quantidades válidas.');
      return;
    }

    const updatedProduct = buildUpdatedProduct();
    if (!updatedProduct) {
      alert('Selecione um produto válido.');
      return;
    }

    if (onProductUpdated) {
      console.log('✅ [handleUpdateProduct] updatedProduct montado:', updatedProduct);
      console.log('✅ [handleUpdateProduct] fashionProduct.collection:', updatedProduct.fashionProduct?.collection);
      onProductUpdated(product, updatedProduct);
    }
    onClose?.();
  };

  if (isLoading) {
    return <GlobalLoadingOverlay />;
  }

  return (
    <>
      <Card>
        <FormGrid className="grid grid-cols-6 gap-4 mt-4">
          <FormGroup>
            <Label>Coleção</Label>
            <Select value={selectedCollectionId} onChange={e => setSelectedCollectionId(e.target.value)}>
              <option value="">Todas</option>
              {collectionsData.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Grupo da Coleção</Label>
            <Select
              value={selectedCollectionGroupId}
              onChange={e => setSelectedCollectionGroupId(e.target.value)}
              disabled={!selectedCollectionId || isLoadingGroups || collectionGroupsData.length === 0}
            >
              {isLoadingGroups ? (
                <option value="">Carregando grupos...</option>
              ) : (
                <>
                  <option value="">Todos</option>
                  {collectionGroupsData.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </>
              )}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Família</Label>
            <Select
              value={selectedFamilyId}
              onChange={e => setSelectedFamilyId(e.target.value)}
              disabled={!selectedCollectionId || isLoadingFamilies || familiesData.length === 0}
            >
              {isLoadingFamilies ? (
                <option value="">Carregando famílias...</option>
              ) : (
                <>
                  <option value="">Todas</option>
                  {familiesData.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </>
              )}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Grupo da Família</Label>
            <Select
              value={selectedFamilyGroupId}
              onChange={e => setSelectedFamilyGroupId(e.target.value)}
              disabled={!selectedFamilyId || familyGroupsData.length === 0}
            >
              <option value="">Todos</option>
              {familyGroupsData.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Produto</Label>
            <Select value={selectedProductId ?? ''} onChange={handleProductSelectChange}>
              <option value="">Selecione o Produto</option>
              {filteredProducts.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label className="block text-gray-500">Preço do Produto</Label>
            <div style={{ padding: '10px', fontWeight: '700', color: '#303030' }}>
              {formatCurrency(selectedPrice || 0)}
            </div>
          </FormGroup>
        </FormGrid>

        {selectedProductId && availableColors.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <HeaderLine>
                <Th></Th>
                {availableSizes.map((item: string, index: number) => (
                  <Th key={index}>{item}</Th>
                ))}
              </HeaderLine>
            </thead>
            <tbody>
              {availableColors.map(colorItem => (
                <Tr key={colorItem.id}>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={!!selectedColors[colorItem.id]}
                        onChange={() => {
                          setSelectedColors(prev => {
                            const next = { ...prev, [colorItem.id]: !prev[colorItem.id] };
                            if (prev[colorItem.id]) {
                              setQuantities(prevMap => {
                                const updated = new Map(prevMap);
                                updated.delete(colorItem.id);
                                return updated;
                              });
                            }
                            return next;
                          });
                        }}
                      />
                      {colorItem.name}
                    </div>
                  </Td>
                  {availableSizes.map((sizeItem: string, sizeIndex: number) => (
                    <Td key={sizeIndex}>
                      <InputLabel
                        type="number"
                        min="0"
                        disabled={!selectedColors[colorItem.id]}
                        value={quantities.get(colorItem.id)?.[sizeItem] || '0'}
                        onChange={e => {
                          const value = parseInt(e.target.value);
                          setQuantities(prevMap => {
                            const updated = new Map(prevMap);
                            if (!updated.has(colorItem.id)) updated.set(colorItem.id, {});
                            updated.get(colorItem.id)![sizeItem] = isNaN(value) ? 0 : value;
                            return updated;
                          });
                        }}
                      />
                    </Td>
                  ))}
                </Tr>
              ))}
            </tbody>
          </table>
        )}

        {selectedProductId && availableColors.length === 0 && (
          <div style={{ padding: '12px', color: '#888', fontSize: '13px' }}>
            Nenhuma cor disponível. Verifique se a família do produto tem cores cadastradas.
          </div>
        )}

        {selectedProductId && availableColors.length > 0 && availableSizes.length === 0 && (
          <div style={{ padding: '12px', color: '#e67e22', fontSize: '13px' }}>
            Produto sem grades cadastradas. Cadastre os tamanhos no produto.
          </div>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel className="text-gray-500">Cores:</SummaryLabel>
            <SummaryValue>{totalSelectedColors}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel className="text-gray-500">Quantidade de peças:</SummaryLabel>
            <SummaryValue>{calculatedTotalQuantity}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel className="text-gray-500">Subtotal:</SummaryLabel>
            <SummaryValue>{formatCurrency(calculatedTotalSubtotal)}</SummaryValue>
          </SummaryItem>
        </Summary>

        <Separator />

        <ButtonsDiv>
          {isEditing ? (
            <AddButton onClick={handleUpdateProduct}>
              <AddButtonImg src={whitePlus} />
              <AddButtonText>Salvar Alterações</AddButtonText>
            </AddButton>
          ) : (
            <AddButton onClick={handleAddProduct}>
              <AddButtonImg src={whitePlus} />
              <AddButtonText>Adicionar ao pedido</AddButtonText>
              <Outlet context={{ addedProducts: order, setAddedProducts: setOrder }} />
            </AddButton>
          )}
          <CancelButton onClick={() => onClose?.()}>
            <CancelButtonText>Cancelar</CancelButtonText>
          </CancelButton>
          {isEditing && (
            <RemoveButton onClick={() => onRemoveProduct(order.findIndex(p => p.fashionProductId === product?.fashionProductId))}>
              <RemoveButtonImg src={remove} />
              <RemoveButtontext>Remover</RemoveButtontext>
            </RemoveButton>
          )}
        </ButtonsDiv>
      </Card>
    </>
  );
};

export default PedidoForm;

